/**
 * @fileoverview Unified search button component that handles both desktop and mobile behaviors
 */

import { Component } from './component.js';

/**
 * Unified search button component that adapts behavior based on screen size
 * - Desktop: Shows hover/focus dropdown with predictive search
 * - Mobile: Triggers header drawer search
 */
class SearchButton extends Component {
  requiredRefs = ['searchButton', 'searchDropdown', 'searchOverlay'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /**
   * Flag to track if current interaction is from mouse/touch (vs keyboard)
   * Used to differentiate focus caused by click vs Tab key navigation
   * @type {boolean}
   */
  #isMouseInteraction = false;

  connectedCallback() {
    super.connectedCallback();

    // Detect mouse/touch interactions (fires before focusin)
    // This allows us to differentiate click-caused focus from keyboard navigation
    this.addEventListener('pointerdown', this.#onPointerDown, { capture: true });
    this.addEventListener('pointerup', this.#onPointerUp, { capture: true });

    // Use event delegation for cleaner event management
    this.addEventListener('focusin', this.#onDesktopEvent);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('click', this.#onClick);

    // Listen for clicks outside to close dropdown
    document.addEventListener('click', this.#onDocumentClick);

    // Listen for overlay clicks to close dropdown
    const overlay = this.refs.searchOverlay;
    if (overlay instanceof HTMLElement) {
      overlay.addEventListener('click', this.#onOverlayClick);
    }

    // Listen for header-menu events to coordinate with megamenu system
    document.addEventListener('header-menu:activate', this.#onHeaderMenuActivate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
    this.removeEventListener('pointerup', this.#onPointerUp, { capture: true });
    this.removeEventListener('focusin', this.#onDesktopEvent);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#onDocumentClick);

    const overlay = this.refs.searchOverlay;
    if (overlay instanceof HTMLElement) {
      overlay.removeEventListener('click', this.#onOverlayClick);
    }

    document.removeEventListener('header-menu:activate', this.#onHeaderMenuActivate);

    // Clean up resize observer
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Check if we're on mobile (screen width < 750px)
   * @returns {boolean}
   */
  #isMobile() {
    return window.innerWidth < 750;
  }

  /**
   * Handle pointer down - sets flag to indicate mouse/touch interaction
   * This fires before focusin, allowing us to detect click-caused focus
   */
  #onPointerDown = () => {
    this.#isMouseInteraction = true;
  };

  /**
   * Handle pointer up - resets the mouse interaction flag
   * Uses rAF to ensure click event has been processed first
   */
  #onPointerUp = () => {
    requestAnimationFrame(() => {
      this.#isMouseInteraction = false;
    });
  };

  /**
   * Handle desktop events (focus in)
   * @param {Event} event
   */
  #onDesktopEvent = (event) => {
    if (this.#isMobile()) return;

    // Only activate on focus caused by keyboard navigation (Tab key)
    // Mouse clicks will handle opening via the click handler
    if (event.type === 'focusin') {
      if (!this.#isMouseInteraction) {
        this.#setDesktopState(true);
      }
    }
  };

  /**
   * Handle focus out event (desktop only)
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (this.#isMobile()) return;

    // Only deactivate if focus is moving outside the component
    if (event.relatedTarget instanceof Node && !this.contains(event.relatedTarget)) {
      this.#setDesktopState(false);
    }
  };

  /**
   * Handle click event
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (this.#isMobile()) {
      // Mobile: Dispatch custom event for header drawer to handle
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('header-drawer:toggle-search'));
    } else {
      // Desktop: Toggle dropdown on click
      const button = this.refs.searchButton;
      if (button instanceof HTMLElement && event.target.closest('.search-modal__button')) {
        event.preventDefault();
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        this.#setDesktopState(!isExpanded);
      }
    }
  };

  /**
   * Handle clicks outside the component to close dropdown
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (this.#isMobile()) return;

    // Close dropdown if click is outside the component
    if (!this.contains(event.target)) {
      this.#setDesktopState(false);
    }
  };

  /**
   * Handle header menu activation - deactivate search if other menu is activated
   * @param {CustomEvent} event
   */
  #onHeaderMenuActivate = (event) => {
    if (event.detail && event.detail.target !== this) {
      this.#setDesktopState(false);
    }
  };

  /**
   * Handle overlay clicks to close dropdown
   * @param {MouseEvent} event
   */
  #onOverlayClick = (event) => {
    if (this.#isMobile()) return;

    event.preventDefault();
    this.#setDesktopState(false);
  };

  /**
   * Set desktop dropdown state (activate/deactivate)
   * @param {boolean} isActive - Whether to activate or deactivate
   */
  #setDesktopState(isActive) {
    const button = this.refs.searchButton;
    const dropdown = this.refs.searchDropdown;

    if (button instanceof HTMLElement) {
      button.setAttribute('aria-expanded', isActive.toString());
    }

    if (dropdown instanceof HTMLElement) {
      if (isActive) {
        // Dispatch event to notify other components (e.g., account popover) to close
        document.dispatchEvent(
          new CustomEvent('search-dropdown:activate', {
            detail: { target: this },
          })
        );

        // Prevent body scroll when dropdown is open
        document.body.classList.add('search-dropdown-open');

        // First, expand the predictive search to show results
        const predictiveSearch = dropdown.querySelector('predictive-search-component');
        if (predictiveSearch) {
          if ('expandSearch' in predictiveSearch && typeof predictiveSearch.expandSearch === 'function') {
            predictiveSearch.expandSearch();
          }
        }

        // Calculate actual height after content is expanded
        // Use requestAnimationFrame to ensure layout has completed
        requestAnimationFrame(() => {
          // Measure the inner content element which contains the actual content
          const innerContent = dropdown.querySelector('.search-dropdown__inner');
          const elementToMeasure = innerContent || dropdown;

          // Use scrollHeight to get full content height, or offsetHeight as fallback
          // scrollHeight works even when element is clipped/hidden
          const dropdownHeight = Math.max(elementToMeasure.scrollHeight, elementToMeasure.offsetHeight || 0);
          this.style.setProperty('--submenu-height', `${dropdownHeight}px`);
          this.style.setProperty('--submenu-opacity', '1');

          // Set up ResizeObserver to update height when content changes
          if (innerContent instanceof HTMLElement) {
            this.#resizeObserver?.disconnect();
            this.#resizeObserver = new ResizeObserver((entries) => {
              for (const entry of entries) {
                const target = /** @type {HTMLElement} */ (entry.target);
                const height = entry.borderBoxSize?.[0]?.blockSize ?? target.offsetHeight;
                this.style.setProperty('--submenu-height', `${height}px`);
              }
            });
            this.#resizeObserver.observe(innerContent);
          }
        });
      } else {
        // Allow body scroll when dropdown is closed
        document.body.classList.remove('search-dropdown-open');

        // Disconnect resize observer when closing
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;

        // Collapse immediately
        this.style.setProperty('--submenu-height', '0px');
        this.style.setProperty('--submenu-opacity', '0');

        const predictiveSearch = dropdown.querySelector('predictive-search-component');
        if (predictiveSearch) {
          predictiveSearch.classList.remove('predictive-search--expanded');
        }
      }
    }
  }

  /**
   * Public method to activate (for external control)
   */
  activate() {
    if (!this.#isMobile()) {
      this.#setDesktopState(true);
    }
  }

  /**
   * Public method to deactivate (for external control)
   */
  deactivate() {
    if (!this.#isMobile()) {
      this.#setDesktopState(false);
    }
  }
}

customElements.define('search-button', SearchButton);
